import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Bot } from '@/types/Bot';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, ExternalLink, Star, CheckCircle2, XCircle, 
  Sparkles, Heart, Share2, Play, Globe, Cpu, MessageSquare 
} from 'lucide-react';
import { Breadcrumb } from '@/components/Breadcrumb';
import { VergoLogo } from '@/components/VergoLogo';
import { UserReviews } from '@/components/UserReviews';
import { useFavorites } from '@/hooks/useFavorites';
import { useComparisonContext } from '@/contexts/ComparisonContext';
import { ComparisonButton } from '@/components/ComparisonButton';
import { ThemeToggle } from '@/components/ThemeToggle';
import { motion } from 'framer-motion';

const BotDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bot, setBot] = useState<Bot | null>(null);
  const [loading, setLoading] = useState(true);
  const { toggleFavorite, isFavorite } = useFavorites();
  const { isInComparison, canAddMore, addToComparison, removeFromComparison } = useComparisonContext();

  useEffect(() => {
    const fetchBot = async () => {
      if (!id) return;
      
      try {
        const { data, error } = await supabase
          .from('bots')
          .select(`
            *,
            categories:category_id (name)
          `)
          .eq('id', id)
          .single();

        if (error) throw error;

        if (data) {
          const categoryName = data.categories?.name || data.subcategory || 'General';
          
          const mappedBot: Bot = {
            id: data.id,
            name: data.name,
            description: data.description,
            category: categoryName,
            rating: data.rating || 0,
            reviewCount: data.conversations || 0,
            price: (data.pricing as 'Free' | 'Freemium' | 'Paid') || 'Free',
            features: data.features_array || [],
            tags: data.tags || [],
            popularity: data.fit_index || 0,
            imageUrl: data.thumbnail || '',
            website: data.site_url || data.website_url || '',
            demoUrl: data.preview_url || undefined,
            longDescription: data.description,
            useCases: data.best_use_cases || [],
            pros: data.pros || [],
            cons: data.cons || [],
            modelType: data.model_type,
            loginRequired: data.login_required,
            languageSupported: data.language ? [data.language] : [],
            key_features: data.key_features || [],
            preview_url: data.preview_url,
            site_url: data.site_url,
            thumbnail: data.thumbnail,
            subcategory: data.subcategory,
            best_use_cases: data.best_use_cases,
            fit_index: data.fit_index
          };

          setBot(mappedBot);
        }
      } catch (error) {
        console.error('Error fetching bot:', error);
        navigate('/404');
      } finally {
        setLoading(false);
      }
    };

    fetchBot();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!bot) return null;

  const isFav = isFavorite(bot.id);
  const botInComparison = isInComparison(bot.id);

  const getPriceColor = (price: string) => {
    switch (price) {
      case 'Free': return 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/20';
      case 'Freemium': return 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/20';
      case 'Paid': return 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/20';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": bot.name,
    "description": bot.description,
    "image": bot.imageUrl || bot.thumbnail,
    "url": `${window.location.origin}/bot/${bot.id}`,
    "brand": { "@type": "Brand", "name": bot.name },
    "offers": {
      "@type": "Offer",
      "price": bot.price === 'Free' ? '0' : bot.price === 'Paid' ? '9.99' : '0',
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": bot.rating,
      "reviewCount": bot.reviewCount
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={`${bot.name} - AI Chatbot Review & Details | Vergo`}
        description={`${bot.description} Discover features, pricing, pros & cons. ${bot.rating}⭐ rating from ${bot.reviewCount} users.`}
        keywords={`${bot.name}, AI chatbot, ${bot.category}, ${bot.tags?.join(', ')}, chatbot review`}
        ogImage={bot.imageUrl || bot.thumbnail || '/og-image.png'}
      />

      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-border/50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <VergoLogo size="lg" />
            </Link>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5">
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb
            items={[
              { label: bot.category, href: `/?category=${bot.category}` },
              { label: bot.name }
            ]}
          />

          {/* Top Section */}
          <motion.div 
            className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Left: Main Info */}
            <div className="lg:col-span-2 space-y-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {bot.imageUrl || bot.thumbnail ? (
                    <img 
                      src={bot.imageUrl || bot.thumbnail} 
                      alt={`${bot.name} logo`}
                      className="w-16 h-16 rounded-xl object-cover border border-border/50"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center text-3xl">
                      🤖
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{bot.name}</h1>
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <Badge variant="outline" className={`${getPriceColor(bot.price)} font-medium`}>
                      {bot.price}
                    </Badge>
                    <Badge variant="secondary" className="capitalize">
                      {bot.category}
                    </Badge>
                    {bot.subcategory && (
                      <Badge variant="outline" className="text-muted-foreground">
                        {bot.subcategory}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold text-foreground">{bot.rating}</span>
                      <span className="text-muted-foreground">({bot.reviewCount.toLocaleString()})</span>
                    </div>
                    {bot.modelType && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Cpu className="w-3.5 h-3.5" />
                        <span>{bot.modelType}</span>
                      </div>
                    )}
                    {bot.loginRequired !== undefined && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Globe className="w-3.5 h-3.5" />
                        <span>{bot.loginRequired ? 'Login Required' : 'No Login'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-muted-foreground leading-relaxed">
                {bot.longDescription || bot.description}
              </p>
            </div>

            {/* Right: Actions */}
            <div className="space-y-4">
              <Card className="border-border/50">
                <CardContent className="p-5 space-y-3">
                  <Button size="lg" className="w-full" asChild>
                    <a href={bot.site_url || bot.website} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Visit Website
                    </a>
                  </Button>
                  {(bot.demoUrl || bot.preview_url) && (
                    <Button size="lg" variant="outline" className="w-full" asChild>
                      <a href={bot.demoUrl || bot.preview_url!} target="_blank" rel="noopener noreferrer">
                        <Play className="w-4 h-4 mr-2" />
                        Try Demo
                      </a>
                    </Button>
                  )}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className={`flex-1 ${isFav ? 'text-primary border-primary/50' : ''}`}
                      onClick={() => toggleFavorite(bot.id)}
                    >
                      <Heart className={`w-4 h-4 mr-2 ${isFav ? 'fill-primary' : ''}`} />
                      {isFav ? 'Saved' : 'Save'}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => navigator.clipboard.writeText(window.location.href)}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                  </div>
                  {/* Comparison Button */}
                  <ComparisonButton
                    bot={bot}
                    isInComparison={botInComparison}
                    canAddMore={canAddMore}
                    onAddToComparison={addToComparison}
                    onRemoveFromComparison={removeFromComparison}
                    variant="outline"
                    size="default"
                  />
                </CardContent>
              </Card>

              {bot.languageSupported && bot.languageSupported.length > 0 && (
                <Card className="border-border/50">
                  <CardContent className="p-5">
                    <h4 className="text-sm font-semibold text-muted-foreground mb-2">Languages</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {bot.languageSupported.map((lang, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{lang}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </motion.div>

          <Separator className="my-8" />

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* Key Features */}
              {bot.key_features && bot.key_features.length > 0 && (
                <section>
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Key Features
                  </h2>
                  <Card className="border-border/50">
                    <CardContent className="p-5">
                      <ul className="grid md:grid-cols-2 gap-2.5">
                        {bot.key_features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2.5">
                            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </section>
              )}

              {/* Pros & Cons */}
              {((bot.pros && bot.pros.length > 0) || (bot.cons && bot.cons.length > 0)) && (
                <div className="grid md:grid-cols-2 gap-4">
                  {bot.pros && bot.pros.length > 0 && (
                    <Card className="border-green-500/20 bg-green-500/5">
                      <CardContent className="p-5">
                        <h3 className="font-bold mb-3 text-green-600 dark:text-green-400 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Pros
                        </h3>
                        <ul className="space-y-2">
                          {bot.pros.map((pro, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                              <span>{pro}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {bot.cons && bot.cons.length > 0 && (
                    <Card className="border-red-500/20 bg-red-500/5">
                      <CardContent className="p-5">
                        <h3 className="font-bold mb-3 text-red-600 dark:text-red-400 flex items-center gap-2">
                          <XCircle className="w-4 h-4" />
                          Considerations
                        </h3>
                        <ul className="space-y-2">
                          {bot.cons.map((con, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm">
                              <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                              <span>{con}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Use Cases */}
              {bot.best_use_cases && bot.best_use_cases.length > 0 && (
                <section>
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    Best Use Cases
                  </h2>
                  <Card className="border-border/50">
                    <CardContent className="p-5">
                      <div className="flex flex-wrap gap-2">
                        {bot.best_use_cases.map((useCase, index) => (
                          <Badge key={index} variant="secondary" className="text-sm px-3 py-1">
                            {useCase}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </section>
              )}

              {/* Reviews */}
              <UserReviews botId={bot.id} botName={bot.name} />
            </div>

            {/* Right sidebar */}
            <div className="space-y-4">
              {bot.features && bot.features.length > 0 && (
                <Card className="border-border/50">
                  <CardContent className="p-5">
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Features</h4>
                    <div className="space-y-1.5">
                      {bot.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {bot.tags && bot.tags.length > 0 && (
                <Card className="border-border/50">
                  <CardContent className="p-5">
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Tags</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {bot.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-border py-8 mt-16">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Vergo. Find your perfect AI companion.</p>
        </div>
      </footer>
    </div>
  );
};

export default BotDetail;
